import { useEffect, useRef, useState } from "react";
import { AuditoriaService } from "../services/auditoriaService";
import type { OrgWithClients, ClientWithOrg } from "../types/auditoria-api.types";

const DEFAULT_DEBOUNCE_MS = 350;

type Result = {
  organizations: OrgWithClients[];
  clients: ClientWithOrg[];
  isOrgLoading: boolean;
  isClientLoading: boolean;
};

export function useAuditClientsSearch(
  orgInput: string,
  clientInput: string,
  debounceMs: number = DEFAULT_DEBOUNCE_MS
): Result {
  const [debouncedOrgInput, setDebouncedOrgInput] = useState(orgInput);
  const [debouncedClientInput, setDebouncedClientInput] = useState(clientInput);
  const [organizations, setOrganizations] = useState<OrgWithClients[]>([]);
  const [clients, setClients] = useState<ClientWithOrg[]>([]);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [isClientLoading, setIsClientLoading] = useState(false);
  const orgReqIdRef = useRef(0);
  const clientReqIdRef = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedOrgInput(orgInput), debounceMs);
    return () => window.clearTimeout(t);
  }, [orgInput, debounceMs]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedClientInput(clientInput), debounceMs);
    return () => window.clearTimeout(t);
  }, [clientInput, debounceMs]);

  useEffect(() => {
    const trimmed = debouncedOrgInput.trim();
    if (!trimmed) {
      setOrganizations([]);
      setIsOrgLoading(false);
      return;
    }
    const id = ++orgReqIdRef.current;
    setIsOrgLoading(true);
    AuditoriaService.fetchClientsSearch({ organization_name: trimmed })
      .then((res) => {
        if (id !== orgReqIdRef.current) return;
        setOrganizations(res.data.organizations?.data ?? []);
      })
      .catch(() => {
        if (id !== orgReqIdRef.current) return;
        setOrganizations([]);
      })
      .finally(() => {
        if (id === orgReqIdRef.current) setIsOrgLoading(false);
      });
  }, [debouncedOrgInput]);

  useEffect(() => {
    const trimmed = debouncedClientInput.trim();
    if (!trimmed) {
      setClients([]);
      setIsClientLoading(false);
      return;
    }
    const id = ++clientReqIdRef.current;
    setIsClientLoading(true);
    AuditoriaService.fetchClientsSearch({ client_name: trimmed })
      .then((res) => {
        if (id !== clientReqIdRef.current) return;
        setClients(res.data.clients?.data ?? []);
      })
      .catch(() => {
        if (id !== clientReqIdRef.current) return;
        setClients([]);
      })
      .finally(() => {
        if (id === clientReqIdRef.current) setIsClientLoading(false);
      });
  }, [debouncedClientInput]);

  return { organizations, clients, isOrgLoading, isClientLoading };
}
